import { ThemeProvider, createTheme } from '@mui/material'
import React, { PropsWithChildren, useEffect, useState } from 'react'
import { lightTheme, darkTheme } from '@common/theme';
import { CLEAR_BRAND_PRIMARY, CLEAR_BRAND_PRIMARY_DARK } from '@common-config/clear-brand';
// NOTE: the sonner <Toaster> is intentionally NOT mounted here. BTThemeProvider
// is instantiated 50+ times (nested per dropdown/dialog/injection), and sonner's
// global event bus renders every toast once per mounted Toaster. Each React root
// mounts exactly one <Toaster> at its top level instead (web SPA, popup, new-tab,
// WA content-script host). See src/components/ui/toaster.tsx.

type Props = {}

// Enhanced theme provider that syncs with CSS class system
const getThemeWithCSSVariableSupport = (
  isDarkMode: boolean,
  direction: 'ltr' | 'rtl',
  isClearBrand = false,
) => {
  const mode = isDarkMode ? 'dark' : 'light';
  const baseTheme = isDarkMode ? darkTheme : lightTheme;

  // The clear flow re-points the CSS colour tokens; MUI controls read the JS
  // palette instead, so move primary with them or switches/checkboxes stay the
  // old brand blue.
  const primary = isClearBrand
    ? { main: isDarkMode ? CLEAR_BRAND_PRIMARY_DARK : CLEAR_BRAND_PRIMARY }
    : baseTheme.palette?.primary;

  return createTheme({
    ...baseTheme,
    direction,
    palette: {
      ...baseTheme.palette,
      ...(primary ? { primary } : {}),
      mode,
    },
    components: {
      ...baseTheme.components,
      // Stack spaces its children with a physical `margin-left` by default,
      // which does NOT mirror: in RTL the spacing lands on the outside of the
      // group and the children sit flush against each other (the scheduler's
      // Cancel / Schedule Send buttons had zero gap in Hebrew). `useFlexGap`
      // switches Stack to real CSS `gap`, which is direction-agnostic — this is
      // MUI's own recommended fix for RTL layouts.
      MuiStack: {
        ...(baseTheme.components?.MuiStack ?? {}),
        defaultProps: {
          ...(baseTheme.components?.MuiStack?.defaultProps ?? {}),
          useFlexGap: true,
        },
      },
    },
  });
};

export const getDarkModeFromBodyClass = () => {
  const htmlElement = document.documentElement;
  const bodyElement = document.body;
  const hasDarkClass = htmlElement.classList.contains('dark') || bodyElement.classList.contains('dark');
  return hasDarkClass;
}

const getClearBrandFromDom = (): boolean =>
  document.documentElement.getAttribute('data-clear-flow') === 'true';

const getDirectionFromDom = (): 'ltr' | 'rtl' => {
  const htmlDirection = document.documentElement.getAttribute('dir');
  const bodyDirection = document.body?.getAttribute('dir');
  return htmlDirection === 'rtl' || bodyDirection === 'rtl' ? 'rtl' : 'ltr';
};

export default function BTThemeProvider({ children }: PropsWithChildren<Props>) {
  // Lazy-init from the DOM so the FIRST paint already reflects the real theme/direction. Starting
  // at false/'ltr' and only reading the DOM in useEffect (which runs after paint) gave RTL/dark
  // users one wrong frame — e.g. the nav drawer rendered LEFT then jumped RIGHT. No SSR here (CRA
  // SPA), and document.body exists by mount, so reading the DOM during init is safe.
  const [isDarkMode, setIsDarkMode] = useState(getDarkModeFromBodyClass);
  const [direction, setDirection] = useState<'ltr' | 'rtl'>(getDirectionFromDom);
  const [isClearBrand, setIsClearBrand] = useState(getClearBrandFromDom);

  useEffect(() => {

    setIsDarkMode(getDarkModeFromBodyClass());
    setDirection(getDirectionFromDom());
    setIsClearBrand(getClearBrandFromDom());

    // Set up MutationObserver to watch for class changes on HTML and BODY elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          if (mutation.attributeName === 'class') {
            setIsDarkMode(getDarkModeFromBodyClass());
          }
          if (mutation.attributeName === 'dir') {
            setDirection(getDirectionFromDom());
          }
          if (mutation.attributeName === 'data-clear-flow') {
            setIsClearBrand(getClearBrandFromDom());
          }
        }
      });
    });

    // Start observing both html and body for class / direction changes
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'dir', 'data-clear-flow']
    });
    if (document.body) {
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'dir']
      });
    }

    // Also listen for media query changes as fallback
    // const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    // const handleMediaQueryChange = (e: MediaQueryListEvent) => {
    //   // Only use system preference if no explicit class is set
    //   if (!document.documentElement.classList.contains('dark') && 
    //       !document.documentElement.classList.contains('light')) {
    //     setIsDarkMode(e.matches);
    //   }
    // };

    // mediaQuery.addEventListener('change', handleMediaQueryChange);

    // Cleanup
    return () => {
      observer.disconnect();
      //mediaQuery.removeEventListener('change', handleMediaQueryChange);
    };
  }, []);

  const theme = React.useMemo(() => {
    return getThemeWithCSSVariableSupport(isDarkMode, direction, isClearBrand);
  }, [isDarkMode, direction, isClearBrand]);

  return (
    <ThemeProvider theme={theme}>
      {children}
    </ThemeProvider>
  );
}
